import { WithOData, withOData } from "@odata/withOData";
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { addCompanyIdToUrl } from "../../../contexts/appContext/AppContext.utils";
import { PermissionContext } from "../../../contexts/permissionContext/PermissionContext";
import { ChartType } from "../../charts";
import BarChart, { IBarChartComponentData } from "../../charts/BarChart";
import DoughnutChart, { IDoughnutChartComponentData } from "../../charts/DoughnutChart";
import LineChart, { ILineChartComponentData } from "../../charts/LineChart";
import { IChartTileInfo, IDashboardTileComponentProps } from "../../dashboard";
import DashboardTileHeader from "../../dashboard/DashboardTileHeader";
import { getInfoValue } from "../../smart/FieldInfo";
import { NoData } from "../../table/NoData";
import { useDashboardTile } from "../hooks/useDashboardTile";
import { BackgroundStyled, ChartWrapperStyled } from "./ChartTile.styles";

interface IChartTileTypeRegistry {
    doughnut: IDoughnutChartComponentData;
    bar: IBarChartComponentData;
    line: ILineChartComponentData;
}

type ChartTileType = keyof IChartTileTypeRegistry;

export interface IChartTileData<Type extends ChartTileType = ChartTileType> {
    type: Type;
    chartProps: IChartTileTypeRegistry[Type];
}

function getChartComponent<T extends ChartTileType = ChartTileType>(type: T): any {
    switch (type) {
        case ChartType.Doughnut:
            return DoughnutChart;
        case ChartType.Bar:
            return BarChart;
        case ChartType.Line:
            return LineChart;
        default:
            return null;
    }
}

interface IProps extends IDashboardTileComponentProps<IChartTileInfo>, WithOData {
}

const ChartTile: React.FC<IProps> = ({ inEditMode, info, oData }) => {
    const context = useContext(AppContext);
    const permissionContext = useContext(PermissionContext);
    const chartWrapper = useRef<HTMLDivElement>();
    const { data: tileData, isLoading, hasError } = useDashboardTile<IChartTileData>({
        getTileDataArgs: {
            prop: info.chartData,
            args: { context, permissionContext, oData }
        }
    });
    const [size, setSize] = useState<{ w: number; h: number; }>(null);

    const { t } = useTranslation("Home");

    const calculateSize = useCallback(() => {
        const { height, width } = chartWrapper.current?.getBoundingClientRect() ?? {};
        setSize({ w: width, h: height });
    }, []);

    // calculate exact size for the chart
    useEffect(() => {
        calculateSize();
    }, []);

    const { type, chartProps } = tileData ?? {};
    const Chart = getChartComponent(type);

    const isEmpty = chartProps?.values?.some((v) => {
        if (Array.isArray(v)) {
            return v.length > 0;
        }
        return !!v;
    }) === false;
    const renderChart = !isEmpty && size && Chart;

    let content = (
        <BackgroundStyled backgroundColor={info.backgroundColor}
                          onResize={calculateSize}
                          isLoading={isLoading || hasError}>
            <DashboardTileHeader tooltip={info.tooltip}>
                {info.title}
            </DashboardTileHeader>
            <ChartWrapperStyled ref={chartWrapper} $editMode={inEditMode}>
                {isEmpty && (<NoData noDataText={t("NoData")}
                                     isSimplified/>)}
                {renderChart && (<Chart width={size.w} height={size.h} formatter={info.formatter} responsive={false}
                                          data={chartProps}/>)}
            </ChartWrapperStyled>
        </BackgroundStyled>
    );

    if (info.link && !inEditMode) {
        const to = getInfoValue(info, "link", { context });

        if (to) {
            content = (
                <Link to={addCompanyIdToUrl(to, context)}>{content}</Link>
            );
        }
    }

    return content;
};

export default memo(withOData(ChartTile));